import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api, { getErrorMessage } from '@/services/api';
import { BackButton, Card } from '@/components/common';
import { BrokerNotesPanel } from './BrokerNotesPanel';

/**
 * Route-level wrapper for the country admin's notes about one broker
 * (`/brokers/:id/notes`). The notes themselves live in `BrokerNotesPanel`,
 * which is also droppable straight into the broker detail page.
 *
 * The broker header and the notes are loaded independently on purpose: if the
 * broker lookup fails, the notes still render (and the header failure is shown
 * as itself) rather than the whole page collapsing into one error.
 */

interface BrokerHeader {
  id: string;
  name: string;
  code: string;
}

export function BrokerNotesPage() {
  const { id } = useParams<{ id: string }>();
  const [broker, setBroker] = useState<BrokerHeader | null>(null);
  const [headerError, setHeaderError] = useState<string | null>(null);

  const loadBroker = useCallback(async () => {
    if (!id) return;
    setHeaderError(null);
    try {
      const resp = await api.get<BrokerHeader>(`/brokers/${id}/`);
      setBroker(resp.data);
    } catch (err) {
      // Never invent a name for the broker being written about.
      setBroker(null);
      setHeaderError(getErrorMessage(err));
    }
  }, [id]);

  useEffect(() => {
    void loadBroker();
  }, [loadBroker]);

  if (!id) {
    return (
      <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        No broker was named in the address, so there are no notes to show.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton />

      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {broker ? broker.name : 'Broker notes'}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Internal country-admin notes{broker?.code ? ` · ${broker.code}` : ''}
        </p>
      </div>

      {headerError && (
        <div
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
        >
          <p className="font-semibold">Broker details could not be loaded.</p>
          <p className="mt-1">{headerError}</p>
        </div>
      )}

      <Card>
        <BrokerNotesPanel brokerId={id} brokerName={broker?.name} />
      </Card>
    </div>
  );
}
